import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import DevUserToolbar from './components/DevUserToolbar';
import { PhotoCacheProvider } from './contexts/PhotoCacheContext';
import { ReactionCacheProvider } from './contexts/ReactionCacheContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { HeicSupportProvider } from './contexts/HeicSupportContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { FriendshipProvider } from './contexts/FriendshipContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { EventDispatcher } from './hooks/useEventDispatcher';
import CacheBusBridge from './components/CacheBusBridge';

// Lazy load page components for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const PhotosPage = lazy(() => import('./pages/PhotosPage'));
// AlbumsPage merged into HomePage — /albums redirects to /
// PhotoLibraryPage + MyAlbumsPage are now panes inside MyLibraryPage (/library)
const MyLibraryPage = lazy(() => import('./pages/MyLibraryPage'));
const MyPhotosPage = lazy(() => import('./pages/MyPhotosPage'));
const CreateAlbumPage = lazy(() => import('./pages/CreateAlbumPage'));
const SocialPage = lazy(() => import('./pages/SocialPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PhotosFeedPage = lazy(() => import('./pages/PhotosFeedPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const MapCollectionPage = lazy(() => import('./pages/MapCollectionPage'));

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex flex-col justify-center items-center h-64 gap-4">
    <div className="spinner"></div>
    <span className="text-gray-500 dark:text-gray-400 animate-pulse">Loading...</span>
  </div>
);

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <HeicSupportProvider>
        <ErrorProvider>
          <AuthProvider>
            <UserProvider>
              <NotificationProvider>
                <FriendshipProvider>
                  <PhotoCacheProvider>
                    <ReactionCacheProvider>
                    <CacheBusBridge />
                    <EventDispatcher />
                    <Router>
                      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-500">
                        <Navigation />
                        <main className="w-full mx-auto px-4 sm:px-6 xl:px-8 3xl:px-12 4xl:px-16 pb-8">
                          <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                              <Route path="/" element={<HomePage />} />
                              <Route path="/albums" element={<Navigate to="/" replace />} />
                              <Route path="/my-albums" element={<Navigate to="/library?view=albums" replace />} />
                              <Route path="/library" element={<MyLibraryPage />} />
                              <Route path="/my-photos/:albumId" element={<MyPhotosPage />} />
                              <Route path="/create-album" element={<CreateAlbumPage />} />
                              <Route path="/photos" element={<PhotosFeedPage />} />
                              <Route path="/photos/:albumId" element={<PhotosPage />} />
                              <Route path="/social" element={<SocialPage />} />
                              <Route path="/invite/:token" element={<InvitePage />} />
                              <Route path="/users/:userId" element={<UserProfilePage />} />
                              <Route path="/settings" element={<SettingsPage />} />
                              <Route path="/map" element={<MapPage />} />
                              <Route path="/map/collection" element={<MapCollectionPage />} />
                            </Routes>
                          </Suspense>
                        </main>
                        <DevUserToolbar />
                      </div>
                    </Router>
                    </ReactionCacheProvider>
                  </PhotoCacheProvider>
                </FriendshipProvider>
              </NotificationProvider>
            </UserProvider>
          </AuthProvider>
        </ErrorProvider>
      </HeicSupportProvider>
    </ThemeProvider>
  );
};

export default App;
